import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Plus, ArrowRight, Loader2, Clock, AlertTriangle } from "lucide-react";

const gbp = (n) => `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return "—";
  const x = new Date(String(d).slice(0, 10));
  return isNaN(x) ? d : x.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};
const TONE = {
  "Awaiting payment": "bg-rose-50 text-rose-700 border-rose-200",
  "Partially paid": "bg-amber-50 text-amber-700 border-amber-200",
  "Paid in full": "bg-emerald-50 text-emerald-700 border-emerald-200",
  Overpaid: "bg-sky-50 text-sky-700 border-sky-200",
};

function acceptedQuoteTotal(quotes) {
  const latest = {};
  (quotes || []).filter((q) => !q.superseded && (q.status || "").toLowerCase() === "accepted").forEach((q) => {
    const k = q.quote_number || q.id;
    if (!latest[k] || (q.version || 0) > (latest[k].version || 0)) latest[k] = q;
  });
  const vals = Object.values(latest);
  return vals.length ? vals.reduce((a, q) => a + (Number(q.totals?.total_inc_vat) || 0), 0) : null;
}

export function usePaymentData(legacyId, quotes) {
  const [d, setD] = useState(null);
  const reload = useCallback(() => {
    api.get(`/tasks/${legacyId}/invoices`).then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [legacyId]);
  useEffect(() => { reload(); }, [reload]);
  if (!d) return { loading: true, reload };
  if (d.error) return { error: true, reload };
  const s = d.summary || {};
  const live = (d.items || []).filter((i) => !i.superseded);
  const qTotal = acceptedQuoteTotal(quotes);
  const invTotal = live.length ? s.invoiced || 0 : 0;
  const total = invTotal > 0 ? invTotal : qTotal;
  const totalSource = invTotal > 0 ? "invoices" : qTotal != null ? "accepted quote" : null;
  const held = s.held_deposits || 0;
  const paid = (s.paid || 0) + held;
  const payments = live.flatMap((i) => (i.payments || []).map((p) => ({ ...p, invoice_no: i.invoice_no })))
    .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || String(b.recorded_at || "").localeCompare(String(a.recorded_at || "")));
  let status = null;
  if (total != null && total > 0) {
    status = paid <= 0.005 ? "Awaiting payment" : paid < total - 0.005 ? "Partially paid" : paid > total + 0.005 ? "Overpaid" : "Paid in full";
  }
  const open = live.filter((i) => i.balance > 0.005);
  return { total, totalSource, paid, held, outstanding: total != null ? Math.max(total - paid, 0) : null, status, payments, open, reload, hasInvoices: live.length > 0 };
}

export function PaymentStatusBadge({ status }) {
  if (!status) return null;
  return <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${TONE[status]}`} data-testid="payment-overview-status">{status}</span>;
}

function Figure({ label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className={`text-base font-semibold tabular-nums truncate ${tone}`} data-testid={testid}>{value}</div>
    </div>
  );
}

export function PaymentSummary({ p, legacyOnly }) {
  if (p.loading) return <div className="h-16 rounded-lg bg-slate-50 animate-pulse" />;
  const na = <span className="text-slate-400 font-medium">Unavailable</span>;
  const pct = p.total ? Math.min(100, Math.round((p.paid / p.total) * 100)) : 0;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3" data-testid="payment-overview-summary">
      <div className="grid grid-cols-3 gap-3">
        <Figure label="Order total" value={p.total != null && !p.error ? gbp(p.total) : na} testid="payment-overview-total" />
        <Figure label="Total paid" value={p.error ? na : legacyOnly && p.paid <= 0 ? <span className="text-slate-400 font-medium">Not recorded</span> : gbp(p.paid)} testid="payment-overview-paid" />
        <Figure label="Outstanding" value={p.outstanding != null ? gbp(p.outstanding) : na}
          tone={p.outstanding > 0.005 ? "text-rose-600" : "text-slate-900"} testid="payment-overview-outstanding" />
      </div>
      {p.total > 0 && (
        <>
          <div className="mt-3 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
          <div className="mt-1 flex justify-between text-[11px] text-slate-500"><span>{pct}% paid</span><span>From {p.totalSource}</span></div>
        </>
      )}
      {p.held > 0 && <div className="mt-1 text-[11px] text-sky-600" data-testid="payment-overview-held">Includes {gbp(p.held)} deposit taken by form, not yet on an invoice</div>}
      {p.status === "Overpaid" && (
        <div className="mt-2 flex items-start gap-1.5 rounded-[8px] border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800" data-testid="payment-overview-overpaid-warning">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Payments of {gbp(p.paid)} are more than the {p.totalSource === "invoices" ? "invoice total" : "accepted quote"} of {gbp(p.total)}. Check the invoice amount or the deposit before going further.</span>
        </div>
      )}
    </div>
  );
}

function PaymentRow({ x }) {
  return (
    <li className="flex items-center gap-2.5 py-2" data-testid={`payment-overview-row-${x.id}`}>
      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-800 truncate">{x.method || "Payment"}{x.invoice_no ? <span className="text-slate-400"> · {x.invoice_no}</span> : null}</div>
        <div className="text-[11px] text-slate-500 truncate">{fmtDate(x.date)} · Confirmed{x.recorded_by ? ` · ${x.recorded_by}` : ""}</div>
      </div>
      <div className="text-sm font-medium tabular-nums text-slate-900">{gbp(x.amount)}</div>
    </li>
  );
}

export function RecentPayments({ p, depositDate, finalDate, onViewAll }) {
  if (p.loading || p.error) return null;
  const legacy = !p.payments.length && (depositDate || finalDate);
  return (
    <div data-testid="payment-overview-recent">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Recent payments</h3>
        <span className="text-[11px] text-slate-500">{p.payments.length} payment{p.payments.length === 1 ? "" : "s"}</span>
      </div>
      {p.payments.length > 0 && <ul className="divide-y divide-slate-100">{p.payments.slice(0, 3).map((x) => <PaymentRow key={x.id} x={x} />)}</ul>}
      {legacy && (
        <ul className="mt-1 space-y-1 text-xs text-slate-500" data-testid="payment-overview-legacy">
          {depositDate && <li className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-400" />Deposit recorded {fmtDate(depositDate)} · amount not recorded</li>}
          {finalDate && <li className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-400" />Paid in full recorded {fmtDate(finalDate)} · amount not recorded</li>}
        </ul>
      )}
      {!p.payments.length && !legacy && <div className="mt-1 text-xs text-slate-400">No payments recorded yet.</div>}
      {onViewAll && (
        <button type="button" onClick={onViewAll} className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700" data-testid="payment-overview-view-all">
          View all payments <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export function QuickPayment({ p, legacyId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ inv: "", amount: "", date: new Date().toISOString().slice(0, 10), method: "", note: "" });
  const [busy, setBusy] = useState(false);
  if (p.loading || p.error) return null;
  const inv = p.open.find((i) => i.id === f.inv) || p.open[0];
  const start = () => { const i = p.open[0]; setF({ inv: i?.id || "", amount: i ? String(i.balance) : "", date: new Date().toISOString().slice(0, 10), method: "", note: "" }); setOpen(true); };
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${legacyId}/invoices/${inv.id}/payments`, { amount: Number(f.amount), date: f.date, method: f.method, note: f.note });
      toast.success("Payment recorded");
      setOpen(false); p.reload(); onSaved?.();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not record payment"); }
    finally { setBusy(false); }
  };
  const cls = "w-full rounded-[8px] border border-slate-200 bg-white px-2.5 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500";
  if (!open) {
    return (
      <div className="flex items-center gap-2">
        <button type="button" onClick={start} disabled={!p.open.length} className="inline-flex items-center gap-1.5 rounded-[8px] h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50" data-testid="payment-overview-record-btn">
          <Plus className="w-4 h-4" /> Record payment
        </button>
        {!p.open.length && <span className="text-[11px] text-slate-500">{p.totalSource === "invoices" ? "All invoices are paid" : p.hasInvoices ? "Invoice has no amount yet (Finance tab)" : "Create an invoice first (Finance tab)"}</span>}
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="payment-overview-quick-form">
      {p.open.length > 1 && (
        <select className={cls} value={inv.id} onChange={(e) => { const i = p.open.find((x) => x.id === e.target.value); setF((s) => ({ ...s, inv: i.id, amount: String(i.balance) })); }} data-testid="quick-pay-invoice">
          {p.open.map((i) => <option key={i.id} value={i.id}>{i.invoice_no} · {gbp(i.balance)} due</option>)}
        </select>
      )}
      <div className="text-[11px] text-slate-500">{inv.invoice_no} · balance {gbp(inv.balance)}</div>
      <div className="grid grid-cols-2 gap-2">
        <input className={cls} type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Amount" data-testid="quick-pay-amount" />
        <input className={cls} type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="quick-pay-date" />
      </div>
      <input className={cls} value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} placeholder="Method (e.g. Card, BACS)" data-testid="quick-pay-method" />
      <input className={cls} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (optional)" data-testid="quick-pay-note" />
      <div className="flex gap-2">
        <button type="button" onClick={save} disabled={busy || !(Number(f.amount) > 0)} className="inline-flex items-center gap-1.5 rounded-[8px] h-9 px-3.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50" data-testid="quick-pay-save">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save payment
        </button>
        <button type="button" onClick={() => setOpen(false)} className="rounded-[8px] h-9 px-3.5 border border-slate-200 text-sm text-slate-600 hover:bg-slate-50" data-testid="quick-pay-cancel">Cancel</button>
      </div>
    </div>
  );
}
